// ---------------------------------------------------------------------------
// Background job manager for "Fetch from AEMET" downloads (see aemet.ts and
// useAemetFetch.ts) — a plain module-level store, not tied to any React
// component's lifecycle, so a download keeps running (and its result still
// gets applied) even if the user navigates away entirely: closes the small
// AemetFetchDialog modal, closes EditSubBasinDialog itself, switches to a
// different sub-basin, or opens a different catalog altogether. Each
// sub-basin gets its own independent job, keyed by `subBasinCode` — starting
// a fetch for one sub-basin and then navigating to another to start a
// second one gives two jobs with independent progress/cancel, not a queue.
//
// `setContext` is called by App.tsx on every render with the CURRENT
// project/series/translator and a callback to write a successful result
// back into React state — the only bridge this store needs into the rest of
// the app. Reading `ctx.project`/`ctx.series` at apply time (not at
// download-start time) means a job always applies against whatever the
// project actually looks like when its download finishes, even if the user
// changed something else in the meantime — same "exact date match or
// reject" contract `insertColumnInSeries` already enforces, just evaluated
// later. `ctx` is `null` whenever no project is open (Landing screen) — see
// `cancelAll()`, called when the project is closed so no job is left
// running against a context that's about to disappear.
// ---------------------------------------------------------------------------
import { translateError, type Translate } from "../../i18n/translate.ts";
import { hasSeriesGapInRange, type ProjectSeries } from "../dailySeries.ts";
import type { AemetProvenance } from "../storage.ts";
import type { EvalHidProject, SubBasin } from "../types.ts";
import {
  delay,
  fetchDailyValues,
  fetchDailyValuesForWindows,
  fetchLaneCount,
  fetchStationInventory,
  mergeDailyValuesResults,
  nearestStations,
  sourceRanges,
  type BuiltSeries,
  type DateRange,
  type FetchDailyValuesResult,
  type StationCandidate,
  type StationSelectionMethod,
} from "./aemet.ts";
import {
  applyBatchToProject,
  applyExtendToProject,
  applyToProject,
  mergeProvenance,
  type BatchApplyEntry,
} from "./aemetApply.ts";
import {
  clampToRocioCoverage,
  defaultRangeEnd,
  defaultRangeStart,
  isRestartable,
  rangeInfoForJob,
} from "./aemetRangeMath.ts";
import type { LatLon } from "../geo.ts";
import { subBasinCentroid, subBasinGeometry, type SubBasinLayer } from "../gisLayer.ts";
import {
  cellsInPolygon,
  fetchRocioGrid,
  fetchRocioValues,
  fetchRocioValuesForWindows,
  nearestGridCells,
} from "../rocio.ts";
import { buildSeriesForMethod, type SeriesKind } from "../seriesByMethod.ts";
import { missingSeriesFor } from "../subBasinState.ts";
import type { AemetJob, AemetJobPhase } from "./aemetJobTypes.ts";

// Re-exported so every existing `import ... from "./aemetJobStore.ts"` (this
// file's own internal usage below, plus test/aemetJobStore.test.ts's
// `AemetJobPhase` import) keeps working unchanged — see aemetJobTypes.ts's
// own header comment for why the definitions themselves moved there.
export type { AemetJob, AemetJobPhase };

interface AemetJobContext {
  project: EvalHidProject;
  series: ProjectSeries;
  /** Only read for extend-mode jobs (`mergeProvenance`) — a normal fetch always covers the SAME forced range as before, so overwriting with the fresh provenance is already equivalent to merging. */
  aemetProvenance: Record<number, AemetProvenance>;
  t: Translate;
  onApplySuccess: (
    subBasinCode: number,
    project: EvalHidProject,
    series: ProjectSeries,
    provenance: AemetProvenance
  ) => void;
  /**
   * Called once around a `startQueued()` batch (before the first job
   * starts / after the last one settles), if provided — lets the caller
   * pause something expensive that would otherwise run once per sub-basin
   * in the batch. Optional so existing test contexts that don't care about
   * this don't need updating. App.tsx wires these to
   * `driveSyncStore.pauseSync`/`resumeSync` — see `startQueued`'s own
   * comment for why.
   */
  onBatchStart?: () => void;
  onBatchEnd?: () => void;
}

/**
 * How many "rocio" sub-basins `startQueued` runs at once, within one batch
 * (see that method's own comment). Station-based methods stay strictly
 * one-at-a-time because AEMET's API key is a real shared external quota —
 * ROCIO has no such quota (its own `/api/rocio/cells` endpoint reads from
 * Cloudflare R2, no per-key budget behind it), so the only real ceiling is
 * that endpoint's OWN defensive rate limit (`RATE_LIMIT`,
 * functions/api/rocio/cells.ts). Real incident (2026-08-25, Duero: 1728
 * sub-basins): 5 concurrent lanes were assumed to average out to "~5 req/s
 * sustained" against the limit, but that only holds if each request takes
 * ~1s — real R2 read latency is closer to ~300ms, so 5 lanes with no pacing
 * of their own produced ~15.6 req/s in practice (a real batch: 1728
 * requests in ~111s). Client-side pacing to match a low limit made large
 * imports painfully slow, so the fix went the other way: `RATE_LIMIT` was
 * raised to comfortably cover this endpoint's real observed throughput —
 * see that constant's own comment for why a generous limit is safe here
 * (no real external quota behind it, only Cloudflare's own free-tier read
 * ceiling). `startDownload`'s automatic retry-on-failure stays as a safety
 * net for whatever a fixed-window limiter can still reject at a boundary.
 */
const ROCIO_CONCURRENCY = 5;

/**
 * How many times `startDownload`'s ROCIO branch automatically retries a
 * failed window (429, transient network error — `fetchRocioValues` never
 * throws, it reports failures via `failedWindows` instead) before giving up
 * and applying whatever data DID come through. Real incident (2026-08-25,
 * Duero: 1728 sub-basins): a large batch briefly exceeded the server's own
 * rate limit (`RATE_LIMIT`, functions/api/rocio/cells.ts — since raised
 * with real margin over `ROCIO_CONCURRENCY`'s actual observed throughput),
 * and the initial download had NO retry of its own (only the user-facing
 * "Reintentar" button, `retryFailedWindows`, did) — a rate-limited
 * sub-basin silently ended up with an empty series applied, `phase:
 * "report"` looking exactly like success. Kept as a safety net even after
 * the limit was raised — a fixed-window rate limiter can still reject a
 * request right at a window boundary (see functions/_shared/_rateLimit.ts's
 * header comment), and this makes that self-heal instead of costing the
 * sub-basin its data. Backoff grows per attempt
 * (`ROCIO_RETRY_BASE_DELAY_MS * (attempt + 1)`).
 *
 * Raised 3->6 retries / 4s->6s base delay (2026-08-27, reported live): at
 * real Duero scale, a handful of sub-basins (~1% — 18, then 4 of a retried
 * batch) still exhausted the old ~24s total budget (4+8+12s) and came back
 * with genuinely zero data — confirmed transient, not a real dead end: a
 * completely fresh manual retry (the bulk dialog's own "Download all",
 * AemetBulkFetchDialog.tsx) succeeded on the very next attempt every time.
 * A longer automatic budget (up to ~126s: 6+12+18+24+30+36) means more of
 * these self-heal without that manual step, at the cost of a slower give-up
 * for the rare sub-basin that's genuinely unreachable — still bounded, and
 * the manual retry stays available regardless.
 */
const ROCIO_MAX_INITIAL_RETRIES = 6;
const ROCIO_RETRY_BASE_DELAY_MS = 6000;

/**
 * How many extra automatic rounds `startQueuedWithRetry` runs after the
 * initial batch, each re-targeting only whatever sub-basins still have no
 * precipitation/ETP column applied — see that method's own comment. Kept
 * small: a fresh, much smaller retry (just the stragglers, not the full
 * 1728-sub-basin batch) has reliably cleared every one of them on its very
 * first attempt in every real report of this so far, so 2 rounds is already
 * a comfortable margin, not a number tuned against an observed need for more.
 */
const AUTO_RETRY_ROUNDS = 2;

/** Pause before each automatic retry round — long enough to let whatever transient contention caused the original failures (see `startQueuedWithRetry`) actually subside, short enough not to noticeably delay the user seeing their data. */
const AUTO_RETRY_DELAY_MS = 5000;

/** One sub-basin's worth of arguments to `aemetJobStore.start()` — shared shape for `startQueued`'s batch and its callers (AemetBulkFetchDialog.tsx, the MITECO-basin wizard flow). */
export interface AemetQueuedRequest {
  subBasinCode: number;
  subBasinName: string;
  centroid: LatLon;
  opts?: {
    freeStart?: string;
    freeEnd?: string;
    extendRange?: DateRange;
    method?: StationSelectionMethod;
    geometry?: GeoJSON.Geometry | null;
  };
}

/**
 * How often notify() actually fans out to listeners, at most — `patchJob()`
 * fires on every phase change AND every completed date-window, so a large
 * bulk import (a real MITECO import, the Duero: 1728 sub-basins) means
 * 10,000+ notify() calls over one download. Every one of those used to
 * trigger a `useSyncExternalStore` re-render in every subscribed component
 * (AemetJobsPopover.tsx, useAemetJobsCount.ts) — a real contributor to that
 * scenario's own out-of-memory crash. 100ms is well below what's
 * perceptible in a progress UI, but collapses a burst of thousands of
 * fanouts down to a handful per second. `jobsSnapshot`/`getAllJobs()`/
 * `hasActiveJobs()` stay synchronously fresh on every call regardless —
 * only the listener fanout (i.e. when React actually re-renders) is
 * throttled, so nothing here changes the store's synchronous read contract
 * (the snapshot rebuild itself is ALSO lazy now, see `getJobsSnapshot`'s
 * own comment — a separate, unthrottled cost this doesn't cover).
 */
const NOTIFY_THROTTLE_MS = 100;

/**
 * How long a burst of completed downloads gets buffered (`pendingApplies`)
 * before `flushApplies` actually mutates the project — same idea/timescale
 * as `NOTIFY_THROTTLE_MS` and `useProjectDataUpdates.ts`'s own
 * `AEMET_APPLY_THROTTLE_MS`, but for the store's OWN project/series mutation
 * (`applyBatchToProject`), not React's re-render — see `flushApplies`'s own
 * comment for the real incident this fixes.
 */
const APPLY_FLUSH_MS = 250;

/**
 * One completed download's built series, waiting for `flushApplies` to
 * actually apply it — everything `applyResult` used to compute inline
 * before mutating `this.ctx.project`/`series` synchronously, now captured
 * so a whole burst can be merged in one pass instead of one mutation per
 * sub-basin (see `flushApplies`'s own comment).
 */
interface PendingApply {
  job: AemetJob;
  subBasin: SubBasin;
  dates: string[];
  range: DateRange;
  precip: BuiltSeries;
  temp: BuiltSeries;
  eto: BuiltSeries;
  failedWindows: DateRange[];
}

export class AemetJobStore {
  private jobs = new Map<number, AemetJob>();
  private jobsSnapshot: AemetJob[] = [];
  /** Set by notify(), cleared by getJobsSnapshot() — see that method's own comment for why the rebuild itself is lazy now, not just the listener fanout. */
  private jobsSnapshotDirty = false;
  private listeners = new Set<() => void>();
  private fanoutScheduled = false;
  private ctx: AemetJobContext | null = null;
  /**
   * Which job's small dialog (AemetFetchDialog.tsx, hosted by
   * AemetFetchDialogHost.tsx — independent of EditSubBasinDialog.tsx's own
   * lifetime) is currently shown, if any — at most one at a time. Also
   * drives which job's stations get highlighted on the map
   * (AemetStationsLayer.tsx): closing the dialog clears the markers even if
   * the download itself keeps going in the background, so the map doesn't
   * stay cluttered with every job ever started this session.
   */
  private openDialogSubBasinCode: number | null = null;
  /**
   * A completed download's built series, waiting for `flushApplies` — see
   * that method's own comment for why this exists (batching the actual
   * project mutation, not just React's re-render). Keyed by subBasinCode so
   * a second apply for the same sub-basin (a manual "Reintentar aplicar",
   * `retryApply`) before the first flush fires just replaces the pending
   * entry instead of stacking two applies for one sub-basin.
   */
  private pendingApplies = new Map<number, PendingApply>();
  private applyFlushTimeout: ReturnType<typeof setTimeout> | null = null;

  setContext(ctx: AemetJobContext | null): void {
    this.ctx = ctx;
  }

  getOpenDialogSubBasinCode = (): number | null => this.openDialogSubBasinCode;

  openDialog(subBasinCode: number): void {
    this.openDialogSubBasinCode = subBasinCode;
    this.notify();
  }

  /** Only hides the dialog (and clears its station markers) — never touches the job itself, see `dismiss()` for that. */
  closeDialog(): void {
    if (this.openDialogSubBasinCode === null) return;
    this.openDialogSubBasinCode = null;
    this.notify();
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getJob = (subBasinCode: number): AemetJob | undefined => this.jobs.get(subBasinCode);

  getAllJobs = (): AemetJob[] => this.getJobsSnapshot();

  hasActiveJobs(): boolean {
    return this.getJobsSnapshot().some(
      (j) => j.phase === "loading-stations" || j.phase === "downloading"
    );
  }

  /**
   * Rebuilds `jobsSnapshot` only when something's actually changed since the
   * last read, not on every notify() — real gap found by deep-review
   * (2026-08-29): `patchJob` fires on every phase change AND every completed
   * date-window, so a large bulk import (Duero: 1728 sub-basins) means
   * 10,000+ notify() calls over one download; rebuilding a 1728-entry array
   * on EVERY one of those (even though the listener FANOUT was already
   * throttled, see NOTIFY_THROTTLE_MS) is real, wasted O(N) work repeated
   * thousands of times in a row with nothing in between ever reading the
   * result. Still synchronously fresh on demand — a read right after a
   * patch still rebuilds immediately, this only skips rebuilding when
   * nothing asked for it.
   */
  private getJobsSnapshot(): AemetJob[] {
    if (this.jobsSnapshotDirty) {
      this.jobsSnapshot = Array.from(this.jobs.values());
      this.jobsSnapshotDirty = false;
    }
    return this.jobsSnapshot;
  }

  private notify(): void {
    this.jobsSnapshotDirty = true;
    if (this.fanoutScheduled) return;
    this.fanoutScheduled = true;
    setTimeout(() => {
      this.fanoutScheduled = false;
      this.listeners.forEach((l) => l());
    }, NOTIFY_THROTTLE_MS);
  }

  private patchJob(subBasinCode: number, patch: Partial<AemetJob>): void {
    const prev = this.jobs.get(subBasinCode);
    if (!prev) return;
    this.jobs.set(subBasinCode, { ...prev, ...patch });
    this.notify();
  }

  /**
   * `opts.freeStart`/`freeEnd` override the remembered/default free range —
   * used by AemetBulkFetchDialog.tsx so every sub-basin in a batch starts
   * from the SAME shared range the user was asked for once, instead of each
   * independently defaulting to "today minus 30 years" (harmless when a
   * `forcedRange` already applies — `deriveRangeInfo` ignores freeStart/End
   * whenever the project already has a precipitation series, see there).
   * `opts.autoDownload` skips the "review candidates/range, click Start"
   * step and calls `startDownload` itself the moment stations resolve —
   * the bulk dialog doesn't show per-sub-basin station/range review (that
   * detail lives in the sub-basin's own AEMET report, per its own header
   * comment), so there's nothing for the user to review before downloading.
   * `opts.extendRange` puts the job in extend mode (see `AemetJob.extendRange`'s
   * own comment) — mutually exclusive with `freeStart`/`freeEnd` in
   * practice (extend mode ignores them, see `rangeInfoForJob`).
   * `opts.method` picks "nearest" (default) or "idw" — see `AemetJob.method`.
   */
  start(
    subBasinCode: number,
    subBasinName: string,
    centroid: LatLon,
    opts: {
      freeStart?: string;
      freeEnd?: string;
      autoDownload?: boolean;
      extendRange?: DateRange;
      method?: StationSelectionMethod;
      geometry?: GeoJSON.Geometry | null;
    } = {}
  ): void {
    const existing = this.jobs.get(subBasinCode);
    // "pending" is a queued-but-not-yet-started placeholder (see startQueued)
    // — real to reset, unlike an actually running/awaiting-input job.
    if (existing && existing.phase !== "pending" && !isRestartable(existing.phase)) return;
    const runToken = (existing?.runToken ?? 0) + 1;
    const geometry = opts.geometry ?? existing?.geometry ?? null;
    const method = opts.method ?? existing?.method ?? "nearest";
    const rawFreeRange = {
      start: opts.freeStart ?? existing?.freeStart ?? defaultRangeStart(),
      end: opts.freeEnd ?? existing?.freeEnd ?? defaultRangeEnd(),
    };
    const freeRange = method === "rocio" ? clampToRocioCoverage(rawFreeRange) : rawFreeRange;
    this.jobs.set(subBasinCode, {
      subBasinCode,
      subBasinName,
      centroid,
      geometry,
      rocioSourceUpdatedAt: null,
      phase: "loading-stations",
      candidates: [],
      laneCount: 1,
      errorMessage: "",
      freeStart: freeRange.start,
      freeEnd: freeRange.end,
      downloadDone: 0,
      downloadTotal: 0,
      downloadResult: null,
      retrying: false,
      applyErrorMessage: "",
      abortController: null,
      runToken,
      extendRange: opts.extendRange ?? null,
      method,
    });
    this.notify();

    this.resolveCandidates(
      subBasinCode,
      runToken,
      centroid,
      geometry,
      method,
      opts.autoDownload ?? false
    );
  }

  /**
   * Looks up candidates for `method` and patches them into the job — shared
   * by `start()` (a job's very first candidate lookup) and `setMethod()`
   * below (re-looking up candidates when switching BETWEEN a station-based
   * method and "rocio", which draw from two completely different sources
   * and can't just reuse whatever candidates happen to already be sitting
   * in `job.candidates`).
   *
   * "rocio" resolves against the grid's own coordinates
   * (public/rocio/coords.json, fetched+cached by fetchRocioGrid — see its
   * header comment) instead of AEMET's station inventory, and never needs
   * more than 1 lane (no per-key rate limit to spread across — see
   * startDownload's own branch). Candidates are EVERY cell whose center
   * falls inside the sub-basin's own polygon (`cellsInPolygon`) — not an
   * approximation from the centroid — falling back to `nearestGridCells`
   * (centroid + radius, at most 1 cell) only when there's no `geometry` to
   * test against, or the sub-basin is smaller than a single 5km cell (no
   * cell center lands inside it) — otherwise this mirrors the station path
   * exactly, down to reusing the same StationCandidate shape and the same
   * "no-stations"/"range" phase transition.
   */
  private resolveCandidates(
    subBasinCode: number,
    runToken: number,
    centroid: LatLon,
    geometry: GeoJSON.Geometry | null,
    method: StationSelectionMethod,
    autoDownload: boolean
  ): void {
    const candidatesPromise: Promise<{
      candidates: StationCandidate[];
      laneCount: number;
      rocioSourceUpdatedAt: string | null;
    }> =
      method === "rocio"
        ? fetchRocioGrid().then(({ manifest, cells }) => {
            const inPolygon = geometry ? cellsInPolygon(cells, geometry, centroid) : [];
            return {
              candidates:
                inPolygon.length > 0 ? inPolygon : nearestGridCells(centroid, cells, { limit: 1 }),
              laneCount: 1,
              rocioSourceUpdatedAt: manifest.sourceUpdatedAt ?? null,
            };
          })
        : Promise.all([fetchStationInventory(), fetchLaneCount()]).then(([stations, lanes]) => ({
            candidates: nearestStations(centroid, stations),
            laneCount: lanes,
            rocioSourceUpdatedAt: null,
          }));

    candidatesPromise
      .then(({ candidates: found, laneCount, rocioSourceUpdatedAt }) => {
        const job = this.jobs.get(subBasinCode);
        if (!job || job.runToken !== runToken) return; // superseded by a later start(), or dismissed meanwhile.
        this.patchJob(subBasinCode, {
          candidates: found,
          laneCount,
          rocioSourceUpdatedAt,
          phase: found.length === 0 ? "no-stations" : "range",
        });
        if (autoDownload && found.length > 0) this.startDownload(subBasinCode);
      })
      .catch((err) => {
        const job = this.jobs.get(subBasinCode);
        if (!job || job.runToken !== runToken || !this.ctx) return;
        this.patchJob(subBasinCode, {
          errorMessage: translateError(err, this.ctx.t),
          phase: "stations-error",
        });
      });
  }

  /** A placeholder entry for a `startQueued` request not yet reached — see there. Zeroed download/candidate fields, `runToken: 0` so the first real `start()` for this sub-basin always looks "newer" (starts at 1). */
  private pendingJob(req: AemetQueuedRequest): AemetJob {
    return {
      subBasinCode: req.subBasinCode,
      subBasinName: req.subBasinName,
      centroid: req.centroid,
      geometry: req.opts?.geometry ?? null,
      rocioSourceUpdatedAt: null,
      phase: "pending",
      candidates: [],
      laneCount: 1,
      errorMessage: "",
      freeStart: req.opts?.freeStart ?? defaultRangeStart(),
      freeEnd: req.opts?.freeEnd ?? defaultRangeEnd(),
      downloadDone: 0,
      downloadTotal: 0,
      downloadResult: null,
      retrying: false,
      applyErrorMessage: "",
      abortController: null,
      runToken: 0,
      extendRange: req.opts?.extendRange ?? null,
      method: req.opts?.method ?? "nearest",
    };
  }

  /**
   * Starts jobs for `requests` — station-based methods (nearest/idw) ONE AT
   * A TIME, "rocio" ones up to `ROCIO_CONCURRENCY` at once (see that
   * constant's own comment for why the two need different treatment).
   * `start()` alone only paces WITHIN one job's own windows
   * (`WINDOW_SPACING_MS`, aemet.ts) — it has no idea another job for a
   * different sub-basin is ALSO hammering the same shared AEMET API key at
   * the same time, so calling `start()` in a plain loop for N sub-basins
   * gives up to N times the real traffic against AEMET's per-key rate
   * limit; ROCIO has no such shared external quota to protect. Used
   * wherever many sub-basins get fetched in one action
   * (AemetBulkFetchDialog.tsx's "download/extend all", the MITECO-basin
   * wizard flow) — in practice every request in a single call shares the
   * same method (one dialog/wizard action = one method choice), but this
   * still partitions by `opts.method` per-request rather than assuming
   * that, so a hypothetical mixed batch is still handled correctly.
   *
   * Every request gets a "pending" placeholder job UP FRONT, in one pass/
   * notify, before any of them actually starts — otherwise a request sat
   * behind dozens of others (a batch import can easily queue 60+) simply
   * didn't exist in `this.jobs` yet, and so was invisible in
   * AemetJobsPopover.tsx and uncounted by its badge until its turn finally
   * came. `start()`'s own guard treats "pending" as freely restartable
   * (like a finished job), so `runOne` below just calls it normally and it
   * overwrites the placeholder in place.
   *
   * A failure on one sub-basin (no stations, download error) doesn't stop
   * the rest — each sub-basin's outcome is independent, and errors stay
   * visible/retriable per row afterward.
   *
   * If something ELSE already started a job for one of these sub-basins
   * (e.g. the user clicked that row's own button while the queue was still
   * working through earlier ones), no placeholder is created for it and
   * `start()`'s own `isRestartable` guard already makes `runOne`'s call a
   * no-op for that entry — `waitForTerminal` just ends up waiting on
   * whatever's already in flight instead of starting a redundant one, so
   * the queue can't get out of sync with a manual click. Dismissing a still-
   * "pending" row (removing it from `this.jobs` before its turn comes) is
   * likewise respected — `runOne` skips starting it instead of resurrecting
   * it the moment it's reached.
   *
   * Running several "rocio" jobs concurrently is safe with respect to
   * `applyResult`'s read-modify-write of `this.ctx.project`/`series`:
   * `applyResult` is a plain synchronous method (no `await` inside it), so
   * JS's single-threaded run-to-completion guarantee means two concurrent
   * downloads resolving around the same time still can't interleave their
   * own `applyResult` calls — same reasoning `fetchDailyValuesForWindows`
   * (aemet.ts) already relies on for its own multi-lane merge.
   */
  async startQueued(requests: AemetQueuedRequest[]): Promise<void> {
    for (const req of requests) {
      const existing = this.jobs.get(req.subBasinCode);
      if (existing && existing.phase !== "pending" && !isRestartable(existing.phase)) continue;
      this.jobs.set(req.subBasinCode, this.pendingJob(req));
    }
    this.notify();

    const runOne = async (req: AemetQueuedRequest): Promise<void> => {
      if (!this.jobs.has(req.subBasinCode)) return; // dismissed while still pending.
      this.start(req.subBasinCode, req.subBasinName, req.centroid, {
        ...req.opts,
        autoDownload: true,
      });
      await this.waitForTerminal(req.subBasinCode);
    };

    const rocioRequests = requests.filter((req) => req.opts?.method === "rocio");
    const otherRequests = requests.filter((req) => req.opts?.method !== "rocio");

    async function runRocioLane(lane: number): Promise<void> {
      for (let i = lane; i < rocioRequests.length; i += ROCIO_CONCURRENCY) {
        await runOne(rocioRequests[i]);
      }
    }

    // Captured once, up front — not re-read from `this.ctx` at the end —
    // so a pause started here always gets its matching resume, even if the
    // project closes (ctx set to null) partway through the batch. Real
    // incident (2026-08-25): each `applyResult` call below eventually
    // triggers the app's own autosave, and so a Google Drive queueSync()
    // (App.tsx wires these to driveSyncStore.pauseSync/resumeSync) — for a
    // large MITECO+ROCIO import (Duero: 1728 sub-basins), that used to mean
    // exportProject() — already the single most expensive step in the app
    // for a big project — running once per sub-basin as its own data
    // arrived, instead of once at the end with the final state.
    const onBatchStart = this.ctx?.onBatchStart;
    const onBatchEnd = this.ctx?.onBatchEnd;
    onBatchStart?.();
    try {
      await Promise.all([
        Promise.all(
          Array.from({ length: Math.min(ROCIO_CONCURRENCY, rocioRequests.length) }, (_, lane) =>
            runRocioLane(lane)
          )
        ),
        (async () => {
          // Flushed after EACH one, unlike the ROCIO lanes above — these run
          // strictly one at a time already (a real external AEMET quota, see
          // this method's own header comment), so there's no concurrent
          // burst to batch here, and a later job in this same sequence
          // relies on THIS one's data already being in `this.ctx.series`
          // (`rangeInfoForJob`'s forced-range inheritance — see the
          // regression test this restores: "keeps an earlier job's applied
          // data"). Deferring the flush would silently break that chain.
          for (const req of otherRequests) {
            await runOne(req);
            this.flushApplies();
          }
        })(),
      ]);
    } finally {
      // Real bug this would otherwise cause: `waitForTerminal` resolves as
      // soon as a job's `phase` reaches "report" (set in `startDownload`'s
      // `.then()`, BEFORE `applyResult` even runs now that it just buffers —
      // see `flushApplies`'s own comment), so without this, `onBatchEnd`
      // (Drive sync's `resumeSync` -> `exportProject`) could fire up to
      // `APPLY_FLUSH_MS` before the LAST few sub-basins' data was actually
      // merged into `this.ctx.project` — syncing a project silently missing
      // the tail end of its own batch.
      this.flushApplies();
      onBatchEnd?.();
    }
  }

  /**
   * `startQueued`, plus up to `AUTO_RETRY_ROUNDS` automatic follow-up passes
   * that re-target only whatever sub-basins in `requests` still have no
   * precipitation/ETP column applied afterward — the same check
   * AemetBulkFetchDialog.tsx's own "Completar subcuencas que faltan" uses to
   * decide what to show.
   *
   * Real incident (2026-08, Duero: 1728 sub-basins, ROCIO): even after
   * `withTimeout` (aemet.ts) ruled out a genuinely stuck lane, a real
   * automatic MITECO+ROCIO import has repeatedly (16, then 4, then 17 of
   * 1728) come out of `startQueued` with a small residual of sub-basins that
   * never got data — not hung (their jobs DO reach a terminal phase), just
   * genuinely unlucky under the sustained load a batch that size puts on
   * ROCIO's endpoint, even with `startDownload`'s own ~126s per-sub-basin
   * retry budget already exhausted. Every single time this has been
   * reported, manually reopening "Completar subcuencas que faltan" for just
   * those stragglers (a MUCH smaller, uncontended batch) has downloaded them
   * perfectly on the very first attempt — this automates exactly that same
   * second pass instead of leaving it to the user to notice the gap and
   * trigger it by hand. Used both for the automatic initial download
   * (App.tsx) and AemetBulkFetchDialog.tsx's own bulk buttons, so a large
   * manual "download all"/"extend all" gets the same safety net.
   *
   * Returns whatever's STILL missing after every round — empty in the
   * overwhelming common case, but callers can use a non-empty result to
   * surface a notice instead of the gap going silent again.
   */
  async startQueuedWithRetry(requests: AemetQueuedRequest[]): Promise<AemetQueuedRequest[]> {
    let remaining = requests;
    for (let round = 0; round <= AUTO_RETRY_ROUNDS && remaining.length > 0; round++) {
      if (round > 0) await delay(AUTO_RETRY_DELAY_MS);
      await this.startQueued(remaining);
      remaining = remaining.filter((req) => this.stillMissingRequiredData(req.subBasinCode));
    }
    return remaining;
  }

  /** True if `subBasinCode` still has no precipitation or ETP column applied — the 2 series `validations.ts` treats as required (temperature/pumping stay permissive, see its own comment), so those are what `startQueuedWithRetry` chases. `false` (not worth retrying) whenever there's no context or the sub-basin itself is gone. */
  private stillMissingRequiredData(subBasinCode: number): boolean {
    if (!this.ctx) return false;
    const subBasin = this.ctx.project.subBasins.find((s) => s.subBasinCode === subBasinCode);
    if (!subBasin) return false;
    const missing = missingSeriesFor(this.ctx.series, subBasin);
    return missing.precipitation || missing.etp;
  }

  /**
   * `startQueued`, plus up to `AUTO_RETRY_ROUNDS` automatic follow-up passes
   * — the "Ampliar todo" equivalent of `startQueuedWithRetry` above, which
   * this deliberately does NOT reuse: `stillMissingRequiredData` checks for
   * "no column at ALL", but an "extend" request's sub-basins already have
   * one (from before the extend) — what actually needs re-checking is
   * whether THAT column still has a gap inside `range`, the same one every
   * request in this batch was just asked to fill
   * (`hasSeriesGapInRange`, dailySeries.ts).
   *
   * Real gap this closes: a bulk "Ampliar" on a huge project (Duero, 1728
   * sub-basins) hits the exact same "small residual left over from a big
   * batch" pattern `startQueuedWithRetry`'s own header already documents
   * for the initial download — but "Ampliar todo" (AemetBulkFetchDialog.tsx)
   * used to call plain `startQueued`, with no retry at all, leaving that
   * residual to only be found later (calculation-time truncation warning,
   * see engine.ts's `findTruncatedSubBasins`) with no easy way back to
   * fixing just those sub-basins.
   */
  async startQueuedExtendWithRetry(
    requests: AemetQueuedRequest[],
    range: DateRange
  ): Promise<AemetQueuedRequest[]> {
    let remaining = requests;
    for (let round = 0; round <= AUTO_RETRY_ROUNDS && remaining.length > 0; round++) {
      if (round > 0) await delay(AUTO_RETRY_DELAY_MS);
      await this.startQueued(remaining);
      remaining = remaining.filter((req) => this.stillHasGapInExtendRange(req.subBasinCode, range));
    }
    return remaining;
  }

  /** Whether `subBasinCode`'s precipitation or ETP still has a gap somewhere inside `range` — see `startQueuedExtendWithRetry`'s own comment for why this, not `stillMissingRequiredData`, is the right check for a retry after "extend". `false` (not worth retrying) whenever there's no context or the sub-basin itself is gone. */
  private stillHasGapInExtendRange(subBasinCode: number, range: DateRange): boolean {
    if (!this.ctx) return false;
    const subBasin = this.ctx.project.subBasins.find((s) => s.subBasinCode === subBasinCode);
    if (!subBasin) return false;
    return (
      hasSeriesGapInRange(this.ctx.series.precipitation, subBasin.precipCol, range) ||
      hasSeriesGapInRange(this.ctx.series.etp, subBasin.etpCol, range)
    );
  }

  /**
   * Scans EVERY sub-basin in the current project for one still missing
   * precipitation/ETP (not just whatever `startQueuedWithRetry` was
   * originally given) and queues a fresh attempt for any of those whose
   * centroid resolves against `gisLayer` — used once, right after the
   * automatic MITECO+ROCIO import settles (App.tsx), on top of (not instead
   * of) `startQueuedWithRetry`'s own narrower re-check.
   *
   * Real gap this closes: Step1SubBasins.tsx's own `aemetRequests` builder
   * silently DROPS any sub-basin whose `subBasinCentroid` comes back `null`
   * at wizard time (`project.subBasins.flatMap(sb => centroid ? [...] : [])`)
   * — such a sub-basin is never queued at all, so it has no job for
   * `startQueuedWithRetry` to ever re-check; it just silently never gets
   * fetched, with nothing telling the user. Re-deriving centroid/geometry
   * here, against whatever `gisLayer` looks like now, gives every such
   * sub-basin the same fresh shot a manual "Completar subcuencas que
   * faltan" already gives it today — this only automates that.
   *
   * `freeRange`/`method` are shared across the whole sweep (same choice the
   * original batch used) since callers only ever run this right after a
   * single-method batch — see `AemetQueuedRequest.opts`'s own per-request
   * shape for why a hypothetical mixed-method sweep isn't supported here.
   */
  async sweepMissingSubBasins(
    gisLayer: SubBasinLayer,
    method: StationSelectionMethod,
    freeRange: DateRange
  ): Promise<AemetQueuedRequest[]> {
    if (!this.ctx) return [];
    // Split rather than flatMap-and-drop: a sub-basin with no resolvable
    // centroid is still genuinely missing data — it just can't be QUEUED —
    // so it belongs in the returned "still missing" list too, not silently
    // excluded from it (the exact silent-drop this method exists to fix).
    const requests: AemetQueuedRequest[] = [];
    const unqueueable: AemetQueuedRequest[] = [];
    for (const sb of this.ctx.project.subBasins) {
      if (!this.stillMissingRequiredData(sb.subBasinCode)) continue;
      const centroid = subBasinCentroid(gisLayer, sb.subBasinCode);
      const opts = {
        freeStart: freeRange.start,
        freeEnd: freeRange.end,
        method,
        geometry: subBasinGeometry(gisLayer, sb.subBasinCode),
      };
      if (centroid) {
        requests.push({
          subBasinCode: sb.subBasinCode,
          subBasinName: sb.description,
          centroid,
          opts,
        });
      } else {
        // No real centroid to offer — `centroid` is a placeholder, never
        // actually used to start a fetch for this entry.
        unqueueable.push({
          subBasinCode: sb.subBasinCode,
          subBasinName: sb.description,
          centroid: { lat: NaN, lon: NaN },
          opts,
        });
      }
    }
    if (requests.length === 0) return unqueueable;
    const stillMissingAfterRetry = await this.startQueuedWithRetry(requests);
    return [...unqueueable, ...stillMissingAfterRetry];
  }

  private waitForTerminal(subBasinCode: number): Promise<void> {
    return new Promise((resolve) => {
      const check = () => {
        const job = this.jobs.get(subBasinCode);
        if (!job || isRestartable(job.phase)) {
          unsubscribe();
          resolve();
        }
      };
      const unsubscribe = this.subscribe(check);
      check();
    });
  }

  setFreeStart(subBasinCode: number, value: string): void {
    this.patchJob(subBasinCode, { freeStart: value });
  }

  setFreeEnd(subBasinCode: number, value: string): void {
    this.patchJob(subBasinCode, { freeEnd: value });
  }

  /**
   * "nearest" <-> "idw" is cheap — both read the SAME station candidates
   * already sitting in `job.candidates`, only how they're later blended
   * changes (see applyResult's own comment). Crossing to/from "rocio" is
   * NOT cheap — it's a different candidate source entirely (grid cells vs.
   * stations) — so this re-resolves candidates from scratch in that case,
   * the same lookup `start()` itself does, rather than leaving stale
   * station candidates in place that `startDownload` would otherwise try
   * (and fail) to use as ROCIO cell ids, or vice versa.
   */
  setMethod(subBasinCode: number, method: StationSelectionMethod): void {
    const job = this.jobs.get(subBasinCode);
    if (!job) return;
    if (method === job.method) return;
    const wasRocio = job.method === "rocio";
    if (wasRocio === (method === "rocio")) {
      this.patchJob(subBasinCode, { method });
      return;
    }
    const runToken = job.runToken; // Unchanged — this isn't a fresh start(), just a re-lookup for the same job.
    // Switching TO "rocio" also clamps the free range into its real coverage
    // (see clampToRocioCoverage's own comment) — a range the user picked
    // while on "nearest"/"idw" (no such upper bound) could easily reach past
    // 2022. Switching AWAY from "rocio" leaves the range untouched — station
    // methods have no comparable ceiling to enforce.
    const freeRange =
      method === "rocio"
        ? clampToRocioCoverage({ start: job.freeStart, end: job.freeEnd })
        : { start: job.freeStart, end: job.freeEnd };
    this.patchJob(subBasinCode, {
      method,
      candidates: [],
      phase: "loading-stations",
      freeStart: freeRange.start,
      freeEnd: freeRange.end,
    });
    this.resolveCandidates(subBasinCode, runToken, job.centroid, job.geometry, method, false);
  }

  startDownload(subBasinCode: number): void {
    const job = this.jobs.get(subBasinCode);
    if (!job || !this.ctx) return;
    const { range, windowCount, validRange } = rangeInfoForJob(job, this.ctx.series);
    if (!validRange) return;
    const controller = new AbortController();
    // ROCIO is always "1 window" — a multi-decade range is a single R2-
    // backed request (see fetchRocioValues' own comment on why it doesn't
    // need AEMET's window-splitting/pacing), so the progress bar just shows
    // 0/1 -> 1/1 instead of AEMET's real per-window count.
    const cellIds = job.candidates.map((c) => c.code);
    const downloadRocioWithRetry = async (): Promise<FetchDailyValuesResult> => {
      let result = await fetchRocioValues(range, cellIds, { signal: controller.signal });
      for (
        let attempt = 0;
        attempt < ROCIO_MAX_INITIAL_RETRIES && result.failedWindows.length > 0;
        attempt++
      ) {
        await delay(ROCIO_RETRY_BASE_DELAY_MS * (attempt + 1), controller.signal);
        const retryResult = await fetchRocioValuesForWindows(result.failedWindows, cellIds, {
          signal: controller.signal,
        });
        result = mergeDailyValuesResults(result, retryResult);
      }
      return result;
    };
    const downloadPromise: Promise<FetchDailyValuesResult> =
      job.method === "rocio"
        ? downloadRocioWithRetry()
        : fetchDailyValues(range, cellIds, {
            onProgress: (done, total) =>
              this.patchJob(subBasinCode, { downloadDone: done, downloadTotal: total }),
            signal: controller.signal,
            laneCount: job.laneCount,
          });
    this.patchJob(subBasinCode, {
      phase: "downloading",
      downloadDone: 0,
      downloadTotal: job.method === "rocio" ? 1 : windowCount,
      abortController: controller,
    });
    downloadPromise
      .then((result) => {
        if (job.method === "rocio") this.patchJob(subBasinCode, { downloadDone: 1 });
        this.patchJob(subBasinCode, {
          downloadResult: result,
          phase: "report",
          abortController: null,
        });
        this.applyResult(subBasinCode, result);
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") {
          this.patchJob(subBasinCode, { phase: "range", abortController: null });
          return;
        }
        if (!this.ctx) return;
        this.patchJob(subBasinCode, {
          errorMessage: translateError(err, this.ctx.t),
          phase: "download-error",
          abortController: null,
        });
      });
  }

  cancelDownload(subBasinCode: number): void {
    this.jobs.get(subBasinCode)?.abortController?.abort();
  }

  /** Removes a job entirely (aborting first if it's still in flight) — the global panel's "dismiss", also used when the project is closed (see `cancelAll`). */
  dismiss(subBasinCode: number): void {
    this.jobs.get(subBasinCode)?.abortController?.abort();
    if (this.openDialogSubBasinCode === subBasinCode) this.openDialogSubBasinCode = null;
    if (this.jobs.delete(subBasinCode)) this.notify();
  }

  /**
   * Removes every FINISHED job at once (`isRestartable` — report/
   * no-stations/error-*) in a single pass/notify, leaving active ones
   * (still downloading, or waiting on a date range) untouched — the global
   * panel's own "Clear finished" button, for a batch import (the MITECO
   * wizard tab, or "download all") that leaves dozens of done rows behind
   * instead of one at a time.
   */
  dismissAllFinished(): void {
    const finished = [...this.jobs.values()].filter((j) => isRestartable(j.phase));
    if (finished.length === 0) return;
    for (const j of finished) {
      if (this.openDialogSubBasinCode === j.subBasinCode) this.openDialogSubBasinCode = null;
      this.jobs.delete(j.subBasinCode);
    }
    this.notify();
  }

  /** Aborts and clears every job — called when the project closes (App.tsx) so nothing keeps running against a context that's about to become `null`. Also drops anything buffered by `applyResult` but not yet flushed — those entries hold references into the closing project/series, and applying them against whatever `this.ctx` points to next (a different project) would silently mix the two. */
  cancelAll(): void {
    this.openDialogSubBasinCode = null;
    if (this.applyFlushTimeout !== null) {
      clearTimeout(this.applyFlushTimeout);
      this.applyFlushTimeout = null;
    }
    this.pendingApplies.clear();
    if (this.jobs.size === 0) return;
    this.jobs.forEach((j) => j.abortController?.abort());
    this.jobs.clear();
    this.notify();
  }

  /**
   * Builds this download's series (cheap — scales with the sub-basin's own
   * candidate/date count, not with the project's total sub-basin count) and
   * buffers it for `flushApplies`, instead of mutating `this.ctx.project`/
   * `series` right away. See `flushApplies`'s own comment for why.
   */
  private applyResult(subBasinCode: number, result: FetchDailyValuesResult): void {
    const job = this.jobs.get(subBasinCode);
    if (!job || !this.ctx) return;
    const { range, targetDates } = rangeInfoForJob(job, this.ctx.series);
    const candidateCodes = job.candidates.map((c) => c.code);
    const forMethod = (kind: SeriesKind) =>
      buildSeriesForMethod(
        job.method,
        kind,
        candidateCodes,
        job.candidates,
        result.byStationByDate,
        targetDates,
        job.centroid.lat
      );
    const precip = forMethod("precip");
    const temp = forMethod("temp");
    const eto = forMethod("eto");

    const subBasin = this.ctx.project.subBasins.find((s) => s.subBasinCode === subBasinCode);
    if (!subBasin) {
      this.patchJob(subBasinCode, {
        applyErrorMessage: this.ctx.t("aemetDialog.subBasinDeleted"),
      });
      return;
    }

    this.pendingApplies.set(subBasinCode, {
      job,
      subBasin,
      dates: targetDates,
      range,
      precip,
      temp,
      eto,
      failedWindows: result.failedWindows,
    });
    if (this.applyFlushTimeout === null) {
      this.applyFlushTimeout = setTimeout(() => {
        this.applyFlushTimeout = null;
        this.flushApplies();
      }, APPLY_FLUSH_MS);
    }
  }

  /**
   * Frees a job's raw `downloadResult.byStationByDate` once nothing can ever
   * need it again — the real fix for a Duero-scale (1728 sub-basins) bulk
   * fetch running out of memory (see `AemetJob.downloadResult`'s own header:
   * it used to sit there "never cleared until dismiss()/cancelAll()", one
   * full byStationByDate Map — years of daily records per candidate station
   * — retained per sub-basin, for every sub-basin in the batch, even though
   * only `failedWindows.length` is ever read from it once apply succeeds).
   * Only safe once BOTH retryFailedWindows (gated on `failedWindows.length`)
   * and retryApply (only offered in the UI while `applyErrorMessage` is set)
   * are ruled out — called right after a successful apply, for a job whose
   * download itself had no failed windows to begin with. `downloadResult`
   * stays non-null (`byStationByDate: new Map()` instead of dropping it
   * altogether) so every existing `!job.downloadResult`/`.failedWindows`
   * check downstream (report screen, `retryFailedWindows`' own early return)
   * keeps working unchanged — this only releases the heavy inner data.
   */
  private releaseDownloadResultIfDone(subBasinCode: number): void {
    const job = this.jobs.get(subBasinCode);
    if (!job?.downloadResult) return;
    if (job.downloadResult.byStationByDate.size === 0) return; // Already released.
    if (job.downloadResult.failedWindows.length > 0) return;
    this.patchJob(subBasinCode, {
      downloadResult: { byStationByDate: new Map(), failedWindows: [] },
    });
  }

  private buildProvenance(p: PendingApply): AemetProvenance {
    const freshProvenance: AemetProvenance = {
      stationsUsed: p.job.candidates.map((c) => ({
        code: c.code,
        name: c.name,
        distanceKm: c.distanceKm,
      })),
      method: p.job.method,
      sourceUpdatedAt: p.job.rocioSourceUpdatedAt ?? undefined,
      fetchedAt: new Date().toISOString(),
      dateRange: p.range,
      // Grouped into contiguous runs here, once, right as the fetch's raw
      // per-day sourceByDate is turned into the persisted AemetProvenance —
      // see AemetProvenance's own comment (storage.ts) for why storing the
      // per-day form directly was a real multi-second freeze at Duero scale.
      sourceByDate: {
        precipitation: sourceRanges(p.precip.sourceByDate, p.dates),
        temperature: sourceRanges(p.temp.sourceByDate, p.dates),
        etp: sourceRanges(p.eto.sourceByDate, p.dates),
      },
      failedWindows: p.failedWindows,
    };
    return p.job.extendRange
      ? mergeProvenance(this.ctx!.aemetProvenance[p.job.subBasinCode], freshProvenance)
      : freshProvenance;
  }

  /**
   * Actually mutates `this.ctx.project`/`series` for every download
   * `applyResult` has buffered since the last flush (at most `APPLY_FLUSH_MS`
   * old). Real incident (2026-08-25): `applyResult` used to mutate the
   * project synchronously, once per sub-basin, the instant its own download
   * finished — `applyToProject`'s `insertColumnInSeries`/
   * `updateSubBasinColumn` are each O(project size), so a large MITECO+ROCIO
   * import (Duero: 1728 sub-basins) meant ~5,000 full-project passes in a
   * row (O(N²) overall), a real measured contributor to that scenario's
   * main-thread jank (~3.7s of blocked time, frame gaps over a second long,
   * in the first 30s alone) — independent of how fast/slow the downloads
   * themselves arrive. Buffering a burst and merging it in ONE
   * `applyBatchToProject` pass turns that into O(N) overall.
   *
   * Extend-mode entries (`job.extendRange`) keep the old one-at-a-time path:
   * `extendSeriesRange` grows the shared date axis instead of requiring an
   * exact match, a different operation from the batched
   * `insertColumnsInSeries` — and "extend the date range" is used on at most
   * a handful of sub-basins at once in practice (AemetBulkFetchDialog.tsx),
   * never the 1000+-sub-basin scale this fix targets.
   *
   * Skips any entry whose job was dismissed since it was buffered (a real,
   * if narrow, race the old synchronous-apply code never had to consider) —
   * `dismiss()`/`cancelAll()` only remove the job/jobs, they don't reach
   * into this buffer, so this checks `this.jobs.has(...)` itself.
   */
  private flushApplies(): void {
    if (this.applyFlushTimeout !== null) {
      clearTimeout(this.applyFlushTimeout);
      this.applyFlushTimeout = null;
    }
    if (this.pendingApplies.size === 0 || !this.ctx) return;
    const pending = [...this.pendingApplies.values()].filter((p) =>
      this.jobs.has(p.job.subBasinCode)
    );
    this.pendingApplies.clear();
    if (pending.length === 0) return;

    const extendEntries = pending.filter((p) => p.job.extendRange);
    const normalEntries = pending.filter((p) => !p.job.extendRange);

    for (const p of extendEntries) {
      try {
        const { project, series } = applyExtendToProject(
          this.ctx.project,
          this.ctx.series,
          p.subBasin,
          p.dates,
          p.precip.values,
          p.temp.values,
          p.eto.values
        );
        this.ctx.project = project;
        this.ctx.series = series;
        const provenance = this.buildProvenance(p);
        this.ctx.aemetProvenance = {
          ...this.ctx.aemetProvenance,
          [p.job.subBasinCode]: provenance,
        };
        this.ctx.onApplySuccess(p.job.subBasinCode, project, series, provenance);
        this.patchJob(p.job.subBasinCode, { applyErrorMessage: "" });
        this.releaseDownloadResultIfDone(p.job.subBasinCode);
      } catch (err) {
        this.patchJob(p.job.subBasinCode, { applyErrorMessage: translateError(err, this.ctx.t) });
      }
    }

    // Grouped by a cheap (start, end, length) fingerprint of `dates` — in
    // practice always a single group per flush, since every job in the same
    // MITECO/ROCIO import targets the same forced range (`rangeInfoForJob`);
    // kept general so a flush that happens to straddle two different
    // ranges still applies correctly, just as more than one batched pass.
    const groups = new Map<string, PendingApply[]>();
    for (const p of normalEntries) {
      const key = `${p.dates[0]}|${p.dates[p.dates.length - 1]}|${p.dates.length}`;
      const group = groups.get(key);
      if (group) group.push(p);
      else groups.set(key, [p]);
    }

    for (const group of groups.values()) {
      try {
        const entries: BatchApplyEntry[] = group.map((p) => ({
          subBasin: p.subBasin,
          precipValues: p.precip.values,
          tempValues: p.temp.values,
          etoValues: p.eto.values,
        }));
        // Same "keep our OWN copy of ctx in sync immediately, not just
        // React's state" reasoning the old per-sub-basin code had — see the
        // real bug it fixed, still documented on `PendingApply`'s own apply
        // wiring: `startQueued` moves on to the next job in the same batch
        // before React necessarily re-renders, so the NEXT flush must see
        // THIS one's result already, not a stale pre-flush snapshot.
        const { project, series } = applyBatchToProject(
          this.ctx.project,
          this.ctx.series,
          group[0].dates,
          entries
        );
        this.ctx.project = project;
        this.ctx.series = series;
        for (const p of group) {
          const provenance = this.buildProvenance(p);
          this.ctx.aemetProvenance = {
            ...this.ctx.aemetProvenance,
            [p.job.subBasinCode]: provenance,
          };
          this.ctx.onApplySuccess(p.job.subBasinCode, project, series, provenance);
          this.patchJob(p.job.subBasinCode, { applyErrorMessage: "" });
          this.releaseDownloadResultIfDone(p.job.subBasinCode);
        }
      } catch (err) {
        for (const p of group) {
          this.patchJob(p.job.subBasinCode, { applyErrorMessage: translateError(err, this.ctx.t) });
        }
      }
    }
  }

  retryApply(subBasinCode: number): void {
    const job = this.jobs.get(subBasinCode);
    if (job?.downloadResult) this.applyResult(subBasinCode, job.downloadResult);
  }

  /**
   * Re-downloads just `downloadResult.failedWindows` — the date windows that
   * failed even after `fetchDailyValuesForWindows`'s (or, for a ROCIO job,
   * `fetchRocioValues`'s) own retries — instead of the whole range again.
   * Stays in the "report" phase the whole time (`retrying` is what the
   * dialog checks instead), since the report/apply UI is still valid while
   * this runs: a successful retry just fills in more of the same
   * `downloadResult` and re-applies it. Branches on `job.method` the same
   * way `startDownload` does — a ROCIO job's `failedWindows` holds cell IDs
   * behind `job.candidates`, not AEMET station codes, so retrying it against
   * `fetchDailyValuesForWindows` (AEMET) would silently query the wrong API
   * with the wrong kind of code.
   */
  retryFailedWindows(subBasinCode: number): void {
    const job = this.jobs.get(subBasinCode);
    if (!job || !job.downloadResult || job.downloadResult.failedWindows.length === 0) return;
    const windows = job.downloadResult.failedWindows;
    const controller = new AbortController();
    this.patchJob(subBasinCode, {
      retrying: true,
      downloadDone: 0,
      downloadTotal: windows.length,
      abortController: controller,
    });
    const cellOrStationCodes = job.candidates.map((c) => c.code);
    const retryPromise: Promise<FetchDailyValuesResult> =
      job.method === "rocio"
        ? fetchRocioValuesForWindows(windows, cellOrStationCodes, {
            onProgress: (done, total) =>
              this.patchJob(subBasinCode, { downloadDone: done, downloadTotal: total }),
            signal: controller.signal,
          })
        : fetchDailyValuesForWindows(windows, cellOrStationCodes, {
            onProgress: (done, total) =>
              this.patchJob(subBasinCode, { downloadDone: done, downloadTotal: total }),
            signal: controller.signal,
            laneCount: job.laneCount,
          });
    retryPromise
      .then((retryResult) => {
        const current = this.jobs.get(subBasinCode);
        if (!current?.downloadResult) return; // dismissed meanwhile.
        const merged = mergeDailyValuesResults(current.downloadResult, retryResult);
        this.patchJob(subBasinCode, {
          downloadResult: merged,
          retrying: false,
          abortController: null,
        });
        this.applyResult(subBasinCode, merged);
      })
      .catch((err) => {
        // Both fetchDailyValuesForWindows and fetchRocioValuesForWindows
        // only reject on AbortError (per-window/per-cell failures are
        // already handled internally, see their own comments) — either way,
        // `downloadResult.failedWindows` is untouched, so the list/button
        // just reappears exactly as before this attempt. Still log anything
        // that ISN'T a plain abort — deep-review (2026-08-29) flagged that
        // assumption breaking (a future change to either fetch function, or
        // a genuinely new failure mode) would otherwise vanish with zero
        // trace, same class of gap as aemet.ts/rocio.ts's own fetch retries.
        if (!(err instanceof DOMException && err.name === "AbortError")) {
          console.warn(
            `[aemet] retryFailedWindows for sub-basin ${subBasinCode} rejected unexpectedly:`,
            err
          );
        }
        this.patchJob(subBasinCode, { retrying: false, abortController: null });
      });
  }
}

export const aemetJobStore = new AemetJobStore();
